import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useFeathers } from '../../app/util';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BulkInviteResearchersModal = ({ refreshUsers = () => {}, onClose = () => {} }) => {
  const feathers = useFeathers();
  const [rawEmails, setRawEmails] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sentCount, setSentCount] = useState(0);
  const [failedEmails, setFailedEmails] = useState([]);

  const parsed = useMemo(() => {
    const lines = rawEmails
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const uniqueEmails = [...new Set(lines.map((line) => line.toLowerCase()))];
    const validEmails = uniqueEmails.filter((email) => EMAIL_REGEX.test(email));
    const invalidEmails = uniqueEmails.filter((email) => !EMAIL_REGEX.test(email));

    return { totalLines: lines.length, validEmails, invalidEmails };
  }, [rawEmails]);

  const submittable = parsed.validEmails.length > 0 && !sending;

  const handleSendInvites = async () => {
    if (!submittable || !feathers) return;

    setSending(true);
    setError('');
    setSentCount(0);
    setFailedEmails([]);

    try {
      const results = await Promise.allSettled(
        parsed.validEmails.map((email) =>
          feathers.getService('invite-links').create({
            email,
            starterPermissions: ['researcher'],
          })
        )
      );

      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failed = parsed.validEmails.filter((_, index) => results[index].status === 'rejected');

      setSentCount(successCount);
      setFailedEmails(failed);

      if (successCount > 0) {
        refreshUsers();
      }
    } catch (err) {
      setError(err?.message || 'Failed to send invitations.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Wrapper>
      <Typography variant="h4">Bulk Invite Researchers</Typography>
      <Typography variant="body2" color="text.secondary">
        Add one email per line. These users will be sent invites as level 1 researchers.
      </Typography>
      <TextField
        label="Emails"
        placeholder={'one@email.com\nanother@email.com'}
        multiline
        minRows={8}
        fullWidth
        value={rawEmails}
        onChange={(event) => setRawEmails(event.target.value)}
        disabled={sending}
      />
      <Typography variant="caption" color="text.secondary">
        {`${parsed.totalLines} lines entered, ${parsed.validEmails.length} valid email(s), ${parsed.invalidEmails.length} invalid.`}
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {parsed.invalidEmails.length > 0 && (
        <Alert severity="warning">
          Some lines are not valid emails and will be skipped.
        </Alert>
      )}
      {sentCount > 0 && (
        <Alert severity="success">
          {`Sent ${sentCount} invitation${sentCount === 1 ? '' : 's'}.`}
        </Alert>
      )}
      {failedEmails.length > 0 && (
        <Alert severity="error">
          {`Failed to send ${failedEmails.length} invitation${
            failedEmails.length === 1 ? '' : 's'
          }.`}
        </Alert>
      )}

      <Actions>
        <Button onClick={onClose} disabled={sending}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!submittable}
          onClick={handleSendInvites}
        >
          {sending ? 'Sending...' : 'Send Invitations'}
        </Button>
      </Actions>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 320px;
  width: 480px;
  max-width: 100%;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export default BulkInviteResearchersModal;
